/**
 * Iter 234 — Bouton "Envoyer pour signature Yousign" pour devis et docs Qualiopi.
 *
 * Props:
 *   contextType: "devis" | "qualiopi_document"
 *   contextId: invoice_id ou document_id
 *   onSent?: () => void — callback après envoi réussi
 *   disabled?: boolean
 *   className?: string
 *
 * Le bouton détecte si Yousign est configuré (via /yousign/status) et :
 *  - Si non configuré : alerte avec lien vers Settings
 *  - Si configuré : ouvre un modal pour personnaliser les signataires si besoin
 */
import { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Loader2, FileSignature, X, Plus, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function YousignSendButton({ contextType, contextId, onSent, disabled = false, className = "" }) {
  const { axiosAuth } = useAuth();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [signers, setSigners] = useState([{ first_name: "", last_name: "", email: "", phone_number: "" }]);
  const [signatureLevel, setSignatureLevel] = useState("advanced");
  const [sendEmails, setSendEmails] = useState(true);
  const [sending, setSending] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [sentResult, setSentResult] = useState(null);

  const openModal = async () => {
    setLoadingStatus(true);
    try {
      const r = await axiosAuth().get("/yousign/status");
      setStatus(r.data);
    } finally {
      setLoadingStatus(false);
    }
    setOpen(true);
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const validSigners = signers.filter((s) => s.email).map((s) => ({
        first_name: s.first_name || "Signataire",
        last_name: s.last_name || "",
        email: s.email,
        phone_number: s.phone_number || null,
      }));
      const path = contextType === "devis"
        ? `/yousign/devis/${contextId}/send`
        : `/yousign/qualiopi/${contextId}/send`;
      const r = await axiosAuth().post(path, {
        signers: validSigners.length ? validSigners : undefined,
        signature_level: signatureLevel,
        send_emails: sendEmails,  // Iter 234c
      });
      toast.success("✅ Envoyé pour signature électronique");
      setSentResult(r.data);
      onSent?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        onClick={openModal}
        disabled={disabled}
        className={`inline-flex items-center gap-2 px-3 py-1.5 bg-purple-500/15 hover:bg-purple-500/25 text-purple-300 rounded-lg text-sm font-medium border border-purple-500/30 disabled:opacity-50 ${className}`}
        data-testid={`yousign-send-${contextType}-${contextId}`}
        title="Envoyer pour signature électronique eIDAS via Yousign"
      >
        <FileSignature className="w-4 h-4" />
        Signature électronique
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setOpen(false)}>
          <div className="bg-[#14101c] border border-white/10 rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="yousign-send-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
                <FileSignature className="w-5 h-5 text-purple-300" />
                Envoyer pour signature
              </h3>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/5 rounded-lg text-slate-400">
                <X className="w-4 h-4" />
              </button>
            </div>

            {loadingStatus ? (
              <div className="flex justify-center p-8"><Loader2 className="w-6 h-6 animate-spin text-purple-400" /></div>
            ) : sentResult ? (
              /* Iter 234c — Écran post-envoi avec public_links + statut emails */
              <div data-testid="yousign-sent-result">
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mb-4">
                  <div className="font-semibold text-emerald-300 text-sm mb-1">✅ Procédure envoyée à Yousign</div>
                  <div className="text-xs text-slate-300">Procédure ID : <code className="text-purple-300">{sentResult.procedure_id}</code> · {sentResult.signers?.length || 0} signataire(s)</div>
                </div>
                {(sentResult.public_links || []).map((pl, i) => (
                  <div key={i} className="bg-[#0b0811] border border-white/10 rounded-lg p-3 mb-2" data-testid={`signer-result-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-semibold text-slate-100">{pl.name}</div>
                      <div className="text-xs text-slate-400">{pl.email}</div>
                    </div>
                    <div className="text-[11px] text-slate-500 mb-2">Lien public Blitzz :</div>
                    <div className="flex items-center gap-2 mb-2">
                      <code className="flex-1 text-[10px] text-purple-300 bg-black/30 rounded px-2 py-1 truncate">{pl.blitzz_url}</code>
                      <button onClick={() => { navigator.clipboard.writeText(pl.blitzz_url); toast.success("Copié"); }}
                        className="px-2 py-1 text-[10px] bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 rounded">
                        Copier
                      </button>
                    </div>
                    {(sentResult.emails || []).find((e) => e.email === pl.email) && (
                      <div className="text-[11px]">
                        Email :{" "}
                        <span className={`${(sentResult.emails || []).find((e) => e.email === pl.email).status === "sent"
                          ? "text-emerald-400"
                          : "text-amber-400"}`}>
                          {(sentResult.emails || []).find((e) => e.email === pl.email).status}
                        </span>
                        {(sentResult.emails || []).find((e) => e.email === pl.email).reason && (
                          <span className="text-slate-500"> · {(sentResult.emails || []).find((e) => e.email === pl.email).reason}</span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                <div className="flex justify-end gap-2 pt-3 border-t border-white/5">
                  <button onClick={() => { setSentResult(null); setOpen(false); }}
                    className="px-5 py-2 bg-purple-500 hover:bg-purple-400 text-white rounded-lg text-sm font-semibold"
                    data-testid="yousign-close-result-btn">
                    Fermer
                  </button>
                </div>
              </div>
            ) : !status?.configured ? (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-sm text-amber-200">
                <p className="font-semibold mb-2">⚠️ Yousign n'est pas configuré</p>
                <p className="text-xs mb-3">Pour activer la signature électronique eIDAS, configurez votre clé API Yousign.</p>
                <Link to="/admin/settings" className="text-xs text-amber-300 hover:underline">→ Aller dans Paramètres → API → Yousign</Link>
              </div>
            ) : (
              <>
                <p className="text-sm text-slate-400 mb-4">
                  {status.use_sandbox && (
                    <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 text-xs mr-2">SANDBOX</span>
                  )}
                  Les signataires recevront un email Yousign avec un lien sécurisé. La signature est conforme eIDAS Avancé.
                </p>

                <div className="space-y-3 mb-4">
                  <label className="block text-xs font-medium text-slate-400">Niveau de signature</label>
                  <div className="flex gap-2">
                    {[{ id: "simple", label: "Simple" }, { id: "advanced", label: "Avancée (recommandé)" }, { id: "qualified", label: "Qualifiée" }].map((l) => (
                      <button key={l.id} onClick={() => setSignatureLevel(l.id)}
                        className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium ${signatureLevel === l.id ? "bg-purple-500 text-white" : "bg-[#0b0811] border border-white/10 text-slate-300"}`}
                        data-testid={`yousign-level-${l.id}`}>
                        {l.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Iter 234c — Toggle email avec QR code */}
                {contextType === "qualiopi_document" && (
                  <label className="flex items-start gap-2 mb-4 p-3 bg-[#0b0811] border border-white/10 rounded-lg cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sendEmails}
                      onChange={(e) => setSendEmails(e.target.checked)}
                      className="mt-1"
                      data-testid="yousign-send-emails-toggle"
                    />
                    <div className="text-xs">
                      <div className="text-slate-200 font-medium mb-0.5">📧 Envoyer un email avec QR code aux signataires</div>
                      <div className="text-slate-500 text-[11px]">Email Resend brandé avec QR code pour scanner depuis le téléphone (signature mobile en 30s).</div>
                    </div>
                  </label>
                )}

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-xs font-medium text-slate-400">Signataires (laissez vide pour utiliser le client par défaut)</label>
                    <button onClick={() => setSigners([...signers, { first_name: "", last_name: "", email: "", phone_number: "" }])}
                      className="text-xs text-purple-300 hover:text-purple-200 inline-flex items-center gap-1">
                      <Plus className="w-3 h-3" /> Ajouter
                    </button>
                  </div>
                  <div className="space-y-2">
                    {signers.map((s, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2">
                        <input value={s.first_name} onChange={(e) => updateSigner(i, "first_name", e.target.value, signers, setSigners)}
                          placeholder="Prénom" className="col-span-3 px-2 py-1.5 bg-[#0b0811] border border-white/10 rounded text-xs text-slate-100"
                          data-testid={`yousign-signer-firstname-${i}`} />
                        <input value={s.last_name} onChange={(e) => updateSigner(i, "last_name", e.target.value, signers, setSigners)}
                          placeholder="Nom" className="col-span-3 px-2 py-1.5 bg-[#0b0811] border border-white/10 rounded text-xs text-slate-100" />
                        <input type="email" value={s.email} onChange={(e) => updateSigner(i, "email", e.target.value, signers, setSigners)}
                          placeholder="email@..." className="col-span-3 px-2 py-1.5 bg-[#0b0811] border border-white/10 rounded text-xs text-slate-100"
                          data-testid={`yousign-signer-email-${i}`} />
                        <input value={s.phone_number || ""} onChange={(e) => updateSigner(i, "phone_number", e.target.value, signers, setSigners)}
                          placeholder="+33..." className="col-span-2 px-2 py-1.5 bg-[#0b0811] border border-white/10 rounded text-xs text-slate-100" />
                        <button onClick={() => setSigners(signers.filter((_, j) => j !== i))}
                          className="col-span-1 p-1.5 hover:bg-rose-500/10 rounded text-rose-400">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-white/5">
                  <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200">Annuler</button>
                  <button onClick={sendNow} disabled={sending}
                    className="px-5 py-2 bg-purple-500 hover:bg-purple-400 text-white rounded-lg text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2"
                    data-testid="yousign-send-confirm-btn"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />}
                    Envoyer pour signature
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function updateSigner(i, key, value, signers, setSigners) {
  const next = [...signers];
  next[i] = { ...next[i], [key]: value };
  setSigners(next);
}
